import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import * as Dialog from "@radix-ui/react-dialog";
import { CalendarClock, Play, Trash2, Power, X } from "lucide-react";
import { BusApi } from "../lib/api";
import { qk } from "../lib/query";
import { useToast } from "../lib/toast";
import { ListDetail } from "../components/ListDetail";
import { Button, Pill, EmptyState } from "../components/primitives";
import { PageHeader } from "../components/PageScaffold";
import { relativeTime, isoDate } from "../lib/format";
import { cn } from "../lib/cn";
import type { BusScheduledTask } from "../lib/types";

const VALID_TARGETS = ["admin", "alfred", "kimiko", "prometheus", "hermes", "plutus", "controller", "athena", "atlas", "apollo", "argus", "tank"];

export default function ScheduledTasks() {
  const qc = useQueryClient();
  const toast = useToast();
  const [target, setTarget] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [editing, setEditing] = useState<BusScheduledTask | null>(null);
  const [creating, setCreating] = useState(false);
  // Recurring (cron) and one-time (once) are different beasts — recurring is
  // always-on infra you inspect; one-time becomes a dead row once it fires. Split
  // them so the handful of live cron jobs aren't buried among spent one-shots.
  const [view, setView] = useState<"cron" | "once">("cron");

  const { data: tasks = [], isLoading, error } = useQuery({
    queryKey: qk.bus.scheduledTasks({ target, status }),
    queryFn: () => BusApi.scheduledTasks({ target: target || undefined, status: status || undefined }),
    refetchInterval: 12_000,
  });
  const { data: stats } = useQuery({ queryKey: qk.bus.scheduledTaskStats, queryFn: BusApi.scheduledTaskStats, refetchInterval: 12_000 });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["bus", "scheduled-tasks"] });
    qc.invalidateQueries({ queryKey: qk.bus.scheduledTaskStats });
  };

  const removeMut = useMutation({
    mutationFn: BusApi.deleteScheduledTask,
    onSuccess: () => {
      toast.success("Task deleted");
      setSelectedId(null);
      refresh();
    },
  });
  const runMut = useMutation({
    mutationFn: BusApi.runScheduledTask,
    onSuccess: () => {
      toast.success("Task fired");
      refresh();
    },
    onError: (err) => toast.error("Run failed", String(err)),
  });
  const toggleMut = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: number }) =>
      BusApi.updateScheduledTask(id, { enabled }),
    onSuccess: refresh,
  });

  const cronCount = tasks.filter((t) => t.schedule_type === "cron").length;
  const onceCount = tasks.length - cronCount;
  const shown = tasks.filter((t) => t.schedule_type === view);

  return (
    <div className="h-full flex flex-col min-h-0">
      <PageHeader
        title="Schedules"
        icon={CalendarClock}
        subtitle={
          stats
            ? `${stats.total} total · ${stats.pending} pending · ${stats.due_now} due now · ${stats.fired} fired · ${stats.disabled} disabled`
            : "cron + one-shot deliveries to agents"
        }
        actions={<Button size="sm" onClick={() => { setCreating(true); setEditing(null); }}>+ New Task</Button>}
      />
      <ScheduleTabs view={view} onChange={setView} cronCount={cronCount} onceCount={onceCount} />
      <div className="flex-1 min-h-0 panel mx-5 mb-5 overflow-hidden flex">
        <ListDetail<BusScheduledTask>
          items={shown}
          itemKey={(t) => t.id}
          selectedId={selectedId}
          onSelect={setSelectedId}
          loading={isLoading}
          error={error as Error | null}
          itemHeight={68}
          toolbar={
            <div className="flex items-center gap-2 px-3 py-2">
              <select value={target} onChange={(e) => setTarget(e.target.value)} className="h-7 text-xs w-32">
                <option value="">All targets</option>
                {VALID_TARGETS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-7 text-xs w-28">
                <option value="">All statuses</option>
                <option value="pending">pending</option>
                <option value="fired">fired</option>
                <option value="disabled">disabled</option>
              </select>
              <span className="ml-auto text-[11px] text-fg-subtle font-mono">{shown.length}</span>
            </div>
          }
          emptyState={<EmptyState icon={<CalendarClock className="size-8" />} title={view === "cron" ? "No recurring schedules" : "No one-time schedules"} />}
          renderItem={({ item }) => <TaskRow t={item} />}
          renderDetail={(t) => (
            <TaskDetail
              task={t}
              onEdit={() => { setEditing(t); setCreating(false); }}
              onDelete={() => { if (confirm(`Delete task "${t.name}"?`)) removeMut.mutate(t.id); }}
              onRun={() => runMut.mutate(t.id)}
              onToggle={() => toggleMut.mutate({ id: t.id, enabled: t.enabled === 0 ? 1 : 0 })}
            />
          )}
        />
      </div>

      <TaskFormDialog
        open={creating || editing !== null}
        task={editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        onSaved={refresh}
      />
    </div>
  );
}

function ScheduleTabs({ view, onChange, cronCount, onceCount }: {
  view: "cron" | "once";
  onChange: (v: "cron" | "once") => void;
  cronCount: number;
  onceCount: number;
}) {
  const tabs: Array<{ v: "cron" | "once"; label: string; count: number }> = [
    { v: "cron", label: "Recurring", count: cronCount },
    { v: "once", label: "One-time", count: onceCount },
  ];
  return (
    <div className="flex items-center gap-1 px-5 -mt-1 mb-3" role="tablist" aria-label="Schedule kind">
      {tabs.map(({ v, label, count }) => (
        <button
          key={v}
          type="button"
          role="tab"
          aria-selected={view === v}
          onClick={() => onChange(v)}
          className={cn(
            "flex items-center gap-1.5 h-7 px-3 rounded-md text-xs font-medium transition-colors",
            view === v ? "bg-accent/15 text-accent" : "text-fg-muted hover:text-fg hover:bg-surface-2",
          )}
        >
          {label}
          <span className={cn("font-mono text-[11px]", view === v ? "text-accent/70" : "text-fg-subtle")}>{count}</span>
        </button>
      ))}
    </div>
  );
}

function deliverLabel(d?: BusScheduledTask["deliver_to"]): string | null {
  if (!d) return null;
  if (d.via === "chat") return `→ ${d.platform}:${d.chat_id.length > 14 ? d.chat_id.slice(0, 12) + "…" : d.chat_id}`;
  if (d.via === "agent") return `→ @${d.to}`;
  return "→ self";
}

function TaskRow({ t }: { t: BusScheduledTask }) {
  const tone =
    t.enabled === 0 ? "neutral" :
    t.status === "fired" ? "success" :
    t.status === "disabled" ? "neutral" :
    "accent";
  const labelStatus = t.enabled === 0 ? "disabled" : t.status;
  const dueIn = t.next_fire_at ? relativeTime(t.next_fire_at) : "—";
  const deliver = deliverLabel(t.deliver_to);
  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-sm font-medium text-fg truncate">{t.name || "(unnamed)"}</span>
        <Pill tone={tone}>{labelStatus}</Pill>
        {t.task_kind === "command" && <Pill tone="info">cmd</Pill>}
        {t.task_kind === "agentic" && <Pill tone="warning">agentic</Pill>}
        <span className="ml-auto text-[11px] text-fg-subtle font-mono whitespace-nowrap">→ {t.target_agent}</span>
      </div>
      <div className="flex items-center gap-2 text-xs text-fg-muted">
        <span className="font-mono truncate">
          {t.command ? `$ ${t.command}` : t.schedule_type === "cron" ? t.cron_expression : (t.scheduled_at ? isoDate(t.scheduled_at) : "—")}
        </span>
        {deliver && <span className="font-mono text-fg-subtle truncate">{deliver}</span>}
        <span className="ml-auto text-fg-subtle">next: {dueIn}</span>
        {t.fire_count > 0 && <span className="font-mono text-fg-subtle">·{t.fire_count}</span>}
      </div>
    </div>
  );
}

function TaskDetail({
  task,
  onEdit,
  onDelete,
  onRun,
  onToggle,
}: {
  task: BusScheduledTask;
  onEdit: () => void;
  onDelete: () => void;
  onRun: () => void;
  onToggle: () => void;
}) {
  const { data: runs = [] } = useQuery({
    queryKey: qk.bus.taskRuns({ task_id: task.id }),
    queryFn: () => BusApi.taskRuns({ task_id: task.id, limit: 20 }),
  });
  return (
    <div className="flex flex-col h-full">
      <div className="px-5 pt-4 pb-3 border-b border-border flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-base font-semibold text-fg">{task.name || "(unnamed)"}</h2>
            <Pill tone={task.enabled ? (task.status === "fired" ? "success" : "accent") : "neutral"}>
              {task.enabled ? task.status : "disabled"}
            </Pill>
            <Pill tone="info">→ {task.target_agent}</Pill>
            {task.fire_count > 0 && <Pill>fired ×{task.fire_count}</Pill>}
          </div>
          <div className="text-xs text-fg-muted mt-1.5 space-y-0.5">
            <div>
              {task.schedule_type === "cron" ? (
                <>cron <code className="font-mono">{task.cron_expression}</code> · tz {task.timezone}</>
              ) : (
                <>once at {task.scheduled_at ? isoDate(task.scheduled_at) : "—"}</>
              )}
            </div>
            <div>next fire: {task.next_fire_at ? `${isoDate(task.next_fire_at)} (${relativeTime(task.next_fire_at)})` : "—"}</div>
            {task.last_fired_at && <div>last fired: {relativeTime(task.last_fired_at)}</div>}
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <Button size="sm" variant="secondary" onClick={onRun}><Play className="size-3.5" />Run now</Button>
          <Button size="sm" variant="secondary" onClick={onEdit}>Edit</Button>
          <Button size="sm" variant="ghost" onClick={onToggle} aria-label="Toggle enabled">
            <Power className={cn("size-3.5", task.enabled === 0 && "text-fg-subtle")} />
          </Button>
          <Button size="sm" variant="ghost" onClick={onDelete} aria-label="Delete">
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1.5">Message</div>
          <pre className="panel px-3 py-2 text-sm font-sans whitespace-pre-wrap [overflow-wrap:anywhere]">{task.message}</pre>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1.5">
            Run history ({runs.length})
          </div>
          {runs.length === 0 ? (
            <div className="panel px-3 py-3 text-xs text-fg-muted">No runs yet.</div>
          ) : (
            <ul className="panel divide-y divide-border">
              {runs.map((r) => (
                <li key={r.id} className="px-3 py-2 flex items-center gap-2 text-xs">
                  <span className={cn("size-1.5 rounded-full", r.status === "success" ? "bg-success" : "bg-danger")} />
                  <span className="text-fg">{r.status}</span>
                  <span className="text-fg-muted">→ {r.target_agent}</span>
                  <span className="ml-auto text-fg-subtle font-mono">{relativeTime(r.fired_at)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Modal for create / edit ───────────────────────────────────────────

function TaskFormDialog({
  open,
  task,
  onClose,
  onSaved,
}: {
  open: boolean;
  task: BusScheduledTask | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(task?.name ?? "");
  const [target, setTarget] = useState(task?.target_agent ?? VALID_TARGETS[0]);
  const [message, setMessage] = useState(task?.message ?? "");
  const [scheduleType, setScheduleType] = useState<"once" | "cron">(task?.schedule_type ?? "once");
  const [cron, setCron] = useState(task?.cron_expression ?? "0 9 * * *");
  const [scheduledAt, setScheduledAt] = useState(task?.scheduled_at ?? "");
  const [tz, setTz] = useState(task?.timezone ?? "Asia/Makassar");
  const [saving, setSaving] = useState(false);

  // Reset form whenever the dialog opens with a different task (or for create)
  useEffect(() => {
    if (!open) return;
    setName(task?.name ?? "");
    setTarget(task?.target_agent ?? VALID_TARGETS[0]);
    setMessage(task?.message ?? "");
    setScheduleType(task?.schedule_type ?? "once");
    setCron(task?.cron_expression ?? "0 9 * * *");
    setScheduledAt(task?.scheduled_at ?? "");
    setTz(task?.timezone ?? "Asia/Makassar");
  }, [open, task?.id]);

  async function save() {
    setSaving(true);
    try {
      const body = {
        name,
        target_agent: target,
        message,
        schedule_type: scheduleType,
        cron_expression: scheduleType === "cron" ? cron : undefined,
        scheduled_at: scheduleType === "once" ? scheduledAt : undefined,
        timezone: tz,
      };
      if (task) await BusApi.updateScheduledTask(task.id, body);
      else await BusApi.createScheduledTask(body);
      toast.success(task ? "Task updated" : "Task created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error("Save failed", String(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[200]" />
        <Dialog.Content className="fixed top-[10vh] left-1/2 -translate-x-1/2 z-[300] w-full max-w-lg panel-elevated shadow-xl">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <Dialog.Title className="text-sm font-semibold text-fg">
              {task ? "Edit task" : "New scheduled task"}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button className="text-fg-subtle hover:text-fg p-1" aria-label="Close"><X className="size-3.5" /></button>
            </Dialog.Close>
          </div>
          <div className="px-4 py-3 space-y-3">
            <Field label="Name">
              <input value={name} onChange={(e) => setName(e.target.value)} className="w-full" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Target">
                <select value={target} onChange={(e) => setTarget(e.target.value)} className="w-full">
                  {VALID_TARGETS.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Type">
                <select value={scheduleType} onChange={(e) => setScheduleType(e.target.value as "once" | "cron")} className="w-full">
                  <option value="once">once</option>
                  <option value="cron">cron</option>
                </select>
              </Field>
            </div>
            {scheduleType === "cron" ? (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Cron expression">
                  <input value={cron} onChange={(e) => setCron(e.target.value)} className="w-full font-mono text-xs" placeholder="0 9 * * *" />
                </Field>
                <Field label="Timezone">
                  <input value={tz} onChange={(e) => setTz(e.target.value)} className="w-full font-mono text-xs" />
                </Field>
              </div>
            ) : (
              <Field label="Scheduled at (ISO 8601)">
                <input value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} className="w-full font-mono text-xs" placeholder="2026-06-01T09:00:00+08:00" />
              </Field>
            )}
            <Field label="Message">
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                className="w-full font-sans"
                placeholder="What to deliver"
              />
            </Field>
          </div>
          <div className="px-4 py-3 border-t border-border flex justify-end gap-2">
            <Button variant="secondary" onClick={onClose} size="sm">Cancel</Button>
            <Button onClick={save} size="sm" loading={saving} disabled={!name || !target || !message}>
              {task ? "Save" : "Create"}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider font-semibold text-fg-subtle mb-1">{label}</span>
      {children}
    </label>
  );
}

